import React, { useEffect, useMemo, useRef, useState, } from "react";
import styled from "styled-components";
import { color } from "../templates/ui";
import Paragraph from "../atoms/Paragraph";
import Button from "../atoms/Button";
import ChevronRight from "../atoms/icons/ChevronRight";
import { useSelector } from "react-redux";
import { withModal } from "../../contexts/ModalContext";
import { openSubmitPaymentArrangementModal } from "../../services/modalService";
import ErrorNotice from "../atoms/ErrorNotice";
import ErrorLabel from "../atoms/ErrorLabel";
import { moneyFormat } from "../../services/currencyService";
import PaymentOption from "../atoms/PaymentOption";
import OptionButton from "../atoms/OptionButton";
import { useTranslation, Trans } from "react-i18next";
import { openSelectDossierTypeModal } from "../../services/modalService";
import ChevronLeft from "../atoms/icons/ChevronLeft";
import axios, { errorsTransformer } from "../../Http";
import * as Endpoint from "../../Http";
import { dateFormat, parseDate } from "../../services/dateService";
import Input from "../atoms/Input";
import RequestNotAvailable from "../molecules/RequestNotAvailable";
import Loader from "../atoms/Loader";
import LoaderIcon from "../atoms/icons/Loader";
const StyledForm = styled.form `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0;
  gap: 16px;
  min-height: 0;
`;
const StyledParagraphFirst = styled(Paragraph) `
  margin-bottom: ${props => (props.noGutter ? "0" : "10px")};
  color: #6b6b6b;

  strong {
    color: ${props => props.theme.primary};
  }
`;
const StyledInputFirstItem = styled(Input) `
  width: 100%;
  padding: 10px 15px;
  border: 1px solid #b2babf;
  border-radius: 4px;
  background-color: white;
  margin-bottom: 0;

  &:focus {
    border-bottom-color: #b2babf;
  }

  @media only screen and (min-width: 768px) {
    margin: 0 20px 0 0;
  }
`;
const StyledDateWrapper = styled.div `
  position: relative;
  display: flex;
  width: 100%;

  input {
    background-color: white;
    width: 100%;
  }

  label {
    position: absolute;
    top: 7px;
    left: 10px;
    pointer-events: none;
    color: ${props => props.theme.tertiary};
    font-weight: 600;
    font-family: ${props => props.theme.fontBase};
    transition: all 0.2s;
    font-size: 85%;
    opacity: 1;
    cursor: text;
  }
`;
const StyledInputDate = styled.input `
  transition: border-color 0.2s ease-in-out;
  padding: 10px 15px;
  background-color: white;
  border: 1px solid #b2babf;
  width: 170px;
  box-sizing: border-box;
  outline: none;
  font-size: 16px;
  line-height: 1.2rem;
  color: #69696f;
  border-radius: 4px;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  @media only screen and (min-width: 340px) {
    width: 200px;
  }

  @media only screen and (min-width: 540px) {
    width: 200px;
  }
`;
const FooterContentContainer = styled.div `
  background: ${color.gray.lightest};
  padding: 20px 30px;
  margin: 0 -30px -30px -30px;
  z-index: 3;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;
const StyledChevronRight = styled(ChevronRight) `
  width: 6px;
  height: 12px;
  fill: ${color.body.base};
  margin-right: 10px;
`;
const OptionButtonsWrapper = styled.div `
  padding: 4px;
  background: ${color.gray.light};
  border-radius: 7px;
  display: flex;
  gap: 0;
`;
const SectionTitle = styled.h3 `
  font-weight: 600;
  font-size: 16px;
  margin: 0 0 8px 0;
`;
const FormWrapper = styled.div `
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0;
`;
const PaymentOptions = styled.div `
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 0;
`;
const StyledChevronLeft = styled(ChevronLeft) `
  width: 6px;
  height: 12px;
  fill: ${color.body.base};
  margin-left: 10px;
`;
const INVONED_PURPLE = "#601E6F";
const BackButton = styled(Button) `
  @media only screen and (min-width: 768px) {
    &:hover {
      color: ${INVONED_PURPLE};
    }
  }
`;
const WarningNotice = styled.div `
  background-color: #fff7e6;
  color: #8a6100;
  padding: 15px;
  border-radius: 3px;
  border-left: 6px solid #ffb347;
  margin-top: 10px;
`;
const PaymentArrangementModal = ({ showModal, oldRequest, forMultipleDossiers, totalBalance: propTotalBalance, minimumMonthlyPayment: propMinimumMonthlyPayment, }) => {
    const { t } = useTranslation();
    const { settings, dossier } = useSelector((state) => state.data);
    const [propositions, setPropositions] = useState(null);
    const [isLoadingPropositions, setIsLoadingPropositions] = useState(false);
    const [propositionsError, setPropositionsError] = useState(false);
    const total = propTotalBalance !== undefined && propTotalBalance !== null
        ? propTotalBalance
        : (dossier.afterVerdict
            ? dossier.balance + dossier.balanceBeforeVerdict
            : dossier.balance);
    const dateInputKeyRef = useRef(0);
    const normalizeEmail = (email) => (email || "").trim();
    const debtorEmail = normalizeEmail(dossier.debtor.email);
    const convertToNativeDate = (ddmmyyyy) => {
        if (!ddmmyyyy)
            return "";
        const parts = ddmmyyyy.replace(/_/g, "").split("-");
        if (parts.length !== 3)
            return "";
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    };
    const convertFromNativeDate = (yyyymmdd) => {
        if (!yyyymmdd)
            return "";
        const parts = yyyymmdd.split("-");
        if (parts.length !== 3)
            return "";
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    };
    const getDefaultDate = () => {
        const date = new Date();
        date.setUTCDate(date.getUTCDate() + 1);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    };
    const initialDate = useMemo(() => {
        if (oldRequest === null || oldRequest === void 0 ? void 0 : oldRequest.fullDate) {
            return convertToNativeDate(oldRequest.fullDate);
        }
        return getDefaultDate();
    }, []);
    const [request, setRequest] = useState(() => {
        var _a, _b, _c, _d;
        return ({
            amount: (_a = oldRequest === null || oldRequest === void 0 ? void 0 : oldRequest.amount) !== null && _a !== void 0 ? _a : "",
            email: normalizeEmail((_b = oldRequest === null || oldRequest === void 0 ? void 0 : oldRequest.email) !== null && _b !== void 0 ? _b : debtorEmail),
            fullDate: (_c = oldRequest === null || oldRequest === void 0 ? void 0 : oldRequest.fullDate) !== null && _c !== void 0 ? _c : initialDate,
            forMultipleDossiers: forMultipleDossiers,
            proposition: (_d = oldRequest === null || oldRequest === void 0 ? void 0 : oldRequest.proposition) !== null && _d !== void 0 ? _d : null,
            totalBalance: forMultipleDossiers ? (propTotalBalance !== null && propTotalBalance !== void 0 ? propTotalBalance : null) : null,
            minimumMonthlyPayment: forMultipleDossiers ? (propMinimumMonthlyPayment !== null && propMinimumMonthlyPayment !== void 0 ? propMinimumMonthlyPayment : null) : null,
        });
    });
    const [error, setError] = useState(null);
    const shouldShowPropositions = useMemo(() => dossier.canRequestPaymentPlan &&
        settings.payment_plan_request_proposals_enabled === true, [dossier.canRequestPaymentPlan, settings.payment_plan_request_proposals_enabled]);
    const showPaymentPlanPropositions = useMemo(() => shouldShowPropositions &&
        !propositionsError &&
        propositions !== null &&
        propositions.length > 0, [shouldShowPropositions, propositionsError, propositions]);
    const [option, setOption] = useState(function () {
        const initialShowPropositions = settings.payment_plan_request_proposals_enabled === true;
        if (oldRequest) {
            if (typeof oldRequest.proposition === "number") {
                return "months";
            }
            if (oldRequest.amount && oldRequest.proposition === null) {
                return "choose";
            }
        }
        return initialShowPropositions ? "months" : "choose";
    });
    const handleOptionChange = (e) => {
        if (e.target.value !== "months") {
            setRequest(Object.assign(Object.assign({}, request), { proposition: null, amount: request.amount || null }));
        }
        setOption(e.target.value);
    };
    const validateDate = (dateString) => {
        if (!dateString)
            return null;
        const split = dateString.replace(/_/g, "").split("-");
        const newDate = `${split[2]}-${split[1]}-${split[0]}`;
        const parsedDate = parseDate(newDate);
        const startOfDay = (function (date) {
            date.setUTCHours(0, 0, 0, 0);
            return date;
        })(new Date());
        if (!parsedDate) {
            return t("dossier:arrangement.invalid_date");
        }
        if (parsedDate < startOfDay) {
            return t("dossier:arrangement.date_not_after_today");
        }
        // Max 31 days in advance for all payment arrangements
        const maxDate = new Date();
        maxDate.setUTCDate(maxDate.getUTCDate() + 31);
        if (parsedDate > maxDate) {
            return t("dossier:arrangement.choose_date_before", {
                max_date: dateFormat(maxDate),
            });
        }
        return null;
    };
    const handleChange = (e) => {
        let value = e.target.value;
        if (e.target.name === "fullDate") {
            value = convertFromNativeDate(value);
            // Validate date in real-time
            const dateError = validateDate(value);
            setError(dateError);
        }
        if (e.target.name === "amount") {
            setError(null);
        }
        if (e.target.name === "email") {
            setError(null);
        }
        const newRequest = Object.assign(Object.assign({}, request), { [e.target.name]: value });
        if (newRequest.proposition !== null && propositions && e.target.name !== "fullDate") {
            const selectedProposition = propositions[newRequest.proposition];
            newRequest.amount = selectedProposition.payment_per_month.toString();
            // Date is not pre-filled - user must choose, validation will ensure it's within 10 days
        }
        setRequest(newRequest);
    };
    const normalizedRequestEmail = normalizeEmail(request.email);
    const isEmailChanged = Boolean(normalizedRequestEmail &&
        debtorEmail.toLowerCase() !== normalizedRequestEmail.toLowerCase());
    const minimumMonthlyPayment = propMinimumMonthlyPayment !== undefined && propMinimumMonthlyPayment !== null
        ? propMinimumMonthlyPayment
        : (settings.payment_plan_request_minimum_monthly_payment || 0);
    /* @ts-ignore */
    const reachedMinimumMonthlyPayment = request.amount && request.amount >= minimumMonthlyPayment;
    const formattedMinimumMonthlyPayment = moneyFormat(minimumMonthlyPayment);
    const reachedMaximumMonthlyPayment = request.amount && parseFloat(request.amount) > total;
    const isContinueEnabled = () => {
        if (!normalizedRequestEmail) {
            return false;
        }
        if (request.proposition !== null && option === "months") {
            return true;
        }
        if (!request.fullDate || !request.amount || reachedMaximumMonthlyPayment) {
            return false;
        }
        return true;
    };
    const goToNextStep = (e) => {
        e.preventDefault();
        // Validate date for both custom amounts and propositions
        const dateError = validateDate(request.fullDate);
        if (dateError) {
            setError(dateError);
            return;
        }
        // check if minimum amount is met
        if (!reachedMinimumMonthlyPayment) {
            setError(t("dossier:arrangement.minimum_amount", {
                amount: formattedMinimumMonthlyPayment,
            }));
            return;
        }
        const normalizedRequest = Object.assign(Object.assign({}, request), { email: normalizedRequestEmail });
        setRequest(normalizedRequest);
        openSubmitPaymentArrangementModal(showModal, normalizedRequest, forMultipleDossiers);
    };
    const getAmountError = () => {
        if (reachedMaximumMonthlyPayment) {
            return t("dossier:arrangement.amount_over_current_debt", {
                amount: moneyFormat(total),
            });
        }
        if (request.amount && !reachedMinimumMonthlyPayment && settings.payment_plan_request_minimum_monthly_payment) {
            return t("dossier:arrangement.minimum_amount", {
                amount: "€ " + settings.payment_plan_request_minimum_monthly_payment + ",-",
            });
        }
        return null;
    };
    const getDateError = () => {
        if (!error)
            return null;
        if (error.includes("invalid_date") || error.includes("date_not_after_today") || error.includes("choose_date_before")) {
            return error;
        }
        return null;
    };
    useEffect(() => {
        if (dossier.canRequestPaymentPlan && settings.payment_plan_request_proposals_enabled === true) {
            setIsLoadingPropositions(true);
            setPropositionsError(false);
            // Build query params for multiple dossiers
            const params = new URLSearchParams();
            if (forMultipleDossiers && propTotalBalance !== null && propTotalBalance !== undefined) {
                params.append('total_balance', propTotalBalance.toString());
            }
            if (forMultipleDossiers && propMinimumMonthlyPayment !== null && propMinimumMonthlyPayment !== undefined) {
                params.append('minimum_monthly_payment', propMinimumMonthlyPayment.toString());
            }
            const url = params.toString()
                ? `${Endpoint.PAYMENT_ARRANGEMENT_PROPOSITIONS}?${params.toString()}`
                : Endpoint.PAYMENT_ARRANGEMENT_PROPOSITIONS;
            axios
                .get(url)
                .then(data => {
                setPropositions(data.data.propositions);
                setIsLoadingPropositions(false);
                // If no propositions or empty array, treat as error
                if (!data.data.propositions || data.data.propositions.length === 0) {
                    setPropositionsError(true);
                }
            })
                .catch(err => {
                err = errorsTransformer(err);
                console.error(err);
                setPropositionsError(true);
                setIsLoadingPropositions(false);
            });
        }
    }, [forMultipleDossiers, propTotalBalance, propMinimumMonthlyPayment]);
    if (!dossier.canRequestPaymentPlan) {
        return React.createElement(RequestNotAvailable, null);
    }
    // Switch to "choose" option when propositions error occurs and loading is done
    useEffect(() => {
        if (propositionsError && !isLoadingPropositions) {
            setOption("choose");
        }
    }, [propositionsError, isLoadingPropositions]);
    return (React.createElement(StyledForm, { onSubmit: goToNextStep },
        shouldShowPropositions && (React.createElement(StyledParagraphFirst, null, t("dossier:arrangement.explanation"))),
        shouldShowPropositions && (React.createElement("div", null,
            React.createElement(OptionButtonsWrapper, null,
                React.createElement(OptionButton, { selected: option === "months", onClick: () => {
                        handleOptionChange({
                            target: { value: "months" },
                        });
                    } }, t("dossier:arrangement.choose_proposals", {
                    defaultValue: "Kies een termijn",
                })),
                React.createElement(OptionButton, { selected: option === "choose", onClick: () => {
                        handleOptionChange({
                            target: { value: "choose" },
                        });
                    } }, t("dossier:arrangement.choose_custom", {
                    defaultValue: "Zelf bedrag invullen",
                }))))),
        settings.payment_plan_request_proposals_enabled === true &&
            option === "months" &&
            (isLoadingPropositions || showPaymentPlanPropositions) && (React.createElement(Loader, { isLoading: isLoadingPropositions, loader: React.createElement("div", { style: { display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 0" } },
                React.createElement(LoaderIcon, null)) },
            React.createElement("div", null, propositions &&
                propositions.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement(SectionTitle, null, t("dossier:arrangement.available_payment_options", {
                    defaultValue: "Beschikbare betaalopties",
                })),
                React.createElement(PaymentOptions, null, propositions.map((item, index) => (React.createElement(PaymentOption, { title: item.months === 1
                        ? t("dossier:arrangement.one_time_payment", {
                            defaultValue: "Eenmalige betaling",
                        })
                        : t("dossier:arrangement.months", {
                            count: item.months,
                        }), subtitle: item.months === 1
                        ? t("dossier:arrangement.with_31_days_delay", {
                            defaultValue: "Met maximaal 31 dagen uitstel",
                        })
                        : t("dossier:arrangement.per_month", {
                            defaultValue: "Per maand",
                        }), price: moneyFormat(item.payment_per_month), name: "proposition", id: "payment_duration_" + item.months.toString(), key: index, value: index.toString(), onChange: handleChange, checked: index.toString() ===
                        (request.proposition || "").toString() }))))))))),
        (!isLoadingPropositions && (!shouldShowPropositions || option === "choose")) && (React.createElement("div", null,
            React.createElement(SectionTitle, null, t("dossier:arrangement.monthly_amount", {
                defaultValue: "Voer een maandbedrag in",
            })),
            React.createElement(FormWrapper, null,
                React.createElement(StyledInputFirstItem, { name: "amount", type: "number", step: "0.01", onChange: handleChange, value: request.amount || "", min: 0, max: total }),
                getAmountError() && React.createElement(ErrorLabel, null, getAmountError())))),
        !isLoadingPropositions && (React.createElement(React.Fragment, null,
            React.createElement("div", null,
                React.createElement(SectionTitle, null, t("dossier:arrangement.first_collection_date", {
                    defaultValue: "Eerste betaaldatum",
                })),
                React.createElement(StyledDateWrapper, null,
                    React.createElement(StyledInputDate, { type: "date", name: "fullDate", onChange: handleChange, value: convertToNativeDate(request.fullDate) || initialDate })),
                getDateError() && React.createElement(ErrorLabel, null, getDateError())),
            React.createElement("div", null,
                React.createElement(SectionTitle, null, t("dossier:arrangement.email", { defaultValue: "Voer uw e-mailadres in" })),
                React.createElement(FormWrapper, null,
                    React.createElement(StyledInputFirstItem, { type: "email", name: "email", value: request.email || "", onChange: handleChange, required: true }))),
            isEmailChanged && (React.createElement(WarningNotice, null,
                React.createElement(Trans, { i18nKey: "dossier:arrangement.email_change_warning", values: {
                        currentEmail: debtorEmail,
                        newEmail: normalizedRequestEmail,
                    }, components: { strong: React.createElement("strong", null) }, defaults: "Let op: we werken het e-mailadres van u bij naar <strong>{{newEmail}}</strong>. Vul uw eigen e-mailadres in (niet dat van een bewindvoerder), zodat we bevestigingen naar u sturen." }))),
            !!error && (React.createElement("div", null,
                React.createElement(ErrorNotice, null, error))),
            React.createElement(FooterContentContainer, null,
                dossier.multipleDossiers && (React.createElement(BackButton, { type: "button", tertiary: true, largeFont: true, bold: true, white: true, icon: React.createElement(StyledChevronLeft, { className: "" }), onClick: () => {
                        openSelectDossierTypeModal(showModal, forMultipleDossiers ? "all" : "current", forMultipleDossiers ? propTotalBalance : null, forMultipleDossiers ? propMinimumMonthlyPayment : null);
                    } }, t("common:generic.back"))),
                React.createElement(Button, { disabled: !isContinueEnabled(), type: "submit", tertiary: true, largeFont: true, bold: true, wide: true, reversed: true, icon: React.createElement(StyledChevronRight, null) }, t("dossier:arrangement.request")))))));
};
export default withModal(PaymentArrangementModal);
